import { Injectable, OnDestroy } from '@angular/core'
import { SiteContextParamsService, SiteContextUrlParams, UrlTreeWithSiteContext, User, WindowRef } from '@spartacus/core'
import { DefaultUrlSerializer } from '@angular/router'
import { of, Subscription } from 'rxjs'
import { MbConstants } from '../../shared/mb-constants'
import { CookieKeys } from '../../cms/enums/cookie-keys'
import { CookieService } from 'ngx-cookie-service'
import { CountryBarService } from '../../cms/services/country-bar/country-bar.service'
import noRedirectUrls from '../../cms/services/country-redirect/no-redirect-urls'
import { CountryRedirectService } from '../../cms/services/country-redirect/country-redirect.service'
import { MbUserAccountService } from '../../user/services/mb-user-account.service'
import { filter, switchMap, take, tap } from 'rxjs/operators'

@Injectable({
  providedIn: 'root'
})
export class MbSiteContextUrlSerializer extends DefaultUrlSerializer implements OnDestroy {

  user: User | undefined
  sub = new Subscription()
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly URL_SPLIT: RegExp = /(^[^#?]*)(.*)/

  constructor (protected siteContextParams: SiteContextParamsService,
               protected mbUserService: MbUserAccountService,
               protected cookieService: CookieService,
               protected winRef: WindowRef,
               protected countryBarService: CountryBarService,
               private countryRedirectService: CountryRedirectService) {
    super()
    this.sub.add(this.mbUserService.getUser().pipe(
      switchMap(user => user ? of(user) : this.mbUserService.get()),
      tap(user => this.user = user),
    ).subscribe())
  }

  /**
   * Names of site context parameters encoded in the URL
   */
  protected get urlEncodingParameters (): string[] {
    return this.siteContextParams.getUrlEncodingParameters()
  }

  /**
   * Tells whether any site context parameters should be encoded in the URL
   */
  protected get hasContextInRoutes (): boolean {
    return this.urlEncodingParameters.length > 0
  }

  ngOnDestroy (): void {
    this.sub.unsubscribe()
  }

  /**
   * @override Recognizes the site context parameters encoded in the prefix segments
   * of the given URL.
   *
   * It returns the UrlTree for the given URL shortened by the recognized params, but saves
   * the params' values in the custom property of UrlTree: `siteContext`.
   */
  parse (url: string): UrlTreeWithSiteContext {
    if (this.hasContextInRoutes) {
      const urlWithParams = this.urlExtractContextParameters(url)
      const parsed = super.parse(urlWithParams.url) as UrlTreeWithSiteContext
      this.urlTreeIncludeContextParameters(parsed, urlWithParams.params)
      return parsed
    } else {
      return super.parse(url)
    }
  }

  /**
   * Recognizes the site context parameters encoded in the prefix segments of the given URL.
   *
   * It returns the recognized site context params as well as the
   * URL shortened by the recognized params.
   */
  urlExtractContextParameters (url: string) {
    const [, urlPart, queryPart] = url.match(this.URL_SPLIT) as any

    const segments = urlPart.split('/')
    if (segments[0] === '') {
      segments.shift()
    }
    const params = {} as any

    let paramId = 0
    let segmentId = 0
    while (
      paramId < this.urlEncodingParameters.length &&
      segmentId < segments.length
      ) {
      const paramName = this.urlEncodingParameters[paramId]
      const paramValues = this.siteContextParams.getParamValues(paramName)

      if (paramValues.includes(segments[segmentId])) {
        params[paramName] = segments[segmentId]
        segmentId++
      }
      paramId++
    }

    url = segments.slice(segmentId).join('/') + queryPart
    return { url, params }
  }

  /**
   * @override Serializes the given UrlTree to a string and prepends
   *  to it the current values of the site context parameters.
   */
  serialize (tree: UrlTreeWithSiteContext): string {
    const params = this.urlTreeExtractContextParameters(tree)
    const url = super.serialize(tree)
    return this.urlIncludeContextParameters(url, params)
  }

  /**
   * Returns the site context parameters stored in the custom property
   * of the UrlTree: `siteContext`.
   */
  urlTreeExtractContextParameters (urlTree: UrlTreeWithSiteContext) {
    return urlTree.siteContext || {}
  }

  /**
   * Saves the given site context parameters in the custom property
   * of the given UrlTree: `siteContext`.
   */
  protected urlTreeIncludeContextParameters (urlTree: UrlTreeWithSiteContext,
                                             params: SiteContextUrlParams) {
    urlTree.siteContext = params
  }

  /**
   * Prepends the current values of the site context parameters to the given URL.
   */
  protected urlIncludeContextParameters (url: string,
                                         params: SiteContextUrlParams) {
    if (this.user?.country?.isocode) {
      return this.countryRedirectService.handleUserContext(this.user, url)
    }

    const isNoRedirectUrl = noRedirectUrls.some(noRedirectUrl => url.toLowerCase().includes(noRedirectUrl.toLowerCase()))

    const contextRoutePart = this.urlEncodingParameters
      .map((param) => params[param] ? params[param] : this.siteContextParams.getValue(param))
      .join('/')

    const activeCountry = contextRoutePart.replace('/', '').split('-').shift() ?? MbConstants.FALLBACK_COUNTRY

    const miltenyiCountry = this.cookieService.get(CookieKeys.MILTENYI_COUNTRY)
    const miltenyiSelectCountry = this.cookieService.get(CookieKeys.MILTENYI_SELECT_COUNTRY)

    if (!activeCountry) {
      return this.countryRedirectService.handleNoActiveCountry(contextRoutePart, url, miltenyiCountry, miltenyiSelectCountry)
    }

    if (!this.cookieService.get(CookieKeys.MILTENYI_SELECT_COUNTRY) || isNoRedirectUrl) {
      this.countryBarService.hideCountryBar()
      const noActiveCountryParam = contextRoutePart.length
        ? contextRoutePart
        : miltenyiCountry?.length ? `${miltenyiCountry}-${MbConstants.FALLBACK_LANGUAGE}`
          : miltenyiSelectCountry?.length ? `${miltenyiSelectCountry}-${MbConstants.FALLBACK_LANGUAGE}`
            : `${MbConstants.FALLBACK_COUNTRY}-${MbConstants.FALLBACK_LANGUAGE}`
      return `${noActiveCountryParam}${url}`
    }

    this.countryRedirectService.setNavigationCountryCookieIfNotPresent(activeCountry)
    this.countryRedirectService.setCookieFallbackValuesIfNoShop()

    const navigationCountry = this.cookieService.get(CookieKeys.NAVIGATION_COUNTRY)

    if (!miltenyiCountry) {
      return this.countryRedirectService.handleNoSelectedCountry(contextRoutePart, url, activeCountry, miltenyiSelectCountry)
    }

    if (miltenyiCountry !== navigationCountry) {
      return this.countryRedirectService.handleMiltenyiCountryIsNotNavigationCountry(miltenyiCountry, url)
    }

    if (miltenyiCountry === activeCountry) {
      return this.countryRedirectService.handleSelectedCountryIsActiveCountry(contextRoutePart, url, miltenyiCountry,
        miltenyiSelectCountry, activeCountry, navigationCountry)
    }

    if (miltenyiCountry !== activeCountry) {
      return this.countryRedirectService.handleMiltenyiCountryIsNotActiveCountry(url, miltenyiCountry, activeCountry)
    }

    return `${contextRoutePart}${url}`
  }
}
